<script setup lang="ts">
/**
 * Bloc « Détails » de la fiche produit — colonne GAUCHE, SOUS la galerie.
 *
 * POURQUOI ICI ET PLUS DANS LA COLONNE DROITE : en desktop, la colonne de
 * gauche s'arrête au bas de la galerie et laisse une grande zone vide, pendant
 * que la colonne de droite empile achat + informations sur une hauteur qui ne
 * tient plus dans l'écran. Le contenu éditorial (ce que l'association a écrit
 * sur SON produit) remplit cette zone : il est lu là où l'œil revient après la
 * photo, sans allonger la colonne d'achat.
 *
 * POURQUOI PLUS D'ONGLETS : un onglet par rubrique découpait le produit en
 * cinq ou six volets dont un seul était lisible à la fois. L'acheteur devait
 * cliquer pour savoir si « Composition » disait quelque chose d'utile.
 *
 * ─── Ce qui se replie, et ce qui ne se replie JAMAIS ────────────────────────
 * La DESCRIPTION du produit reste toujours en clair : c'est l'argument de
 * vente, elle se lit sans un geste. Seules les RUBRIQUES libres
 * (« Composition », « Entretien », « Guide des tailles ») sont dépliantes —
 * ce sont des précisions qu'on consulte à la demande, et les empiler en clair
 * repoussait le reste de la page très bas. La PREMIÈRE est ouverte au
 * chargement : la page ne s'ouvre pas sur un mur de titres fermés.
 *
 * ⚠️ Le repli est un MASQUAGE CSS (hauteur animée), jamais un `v-if` : le
 * contenu de toutes les rubriques reste dans le HTML SSR, donc indexable et
 * trouvable au Ctrl+F du navigateur. C'était le gain de la suppression des
 * onglets, il est conservé intégralement.
 *
 * ⚠️ RÈGLE CONSERVÉE : aucune rubrique décorative. Une rubrique sans titre OU
 * sans contenu utile n'est pas rendue, et si le produit n'a NI description
 * longue NI rubrique, le bloc entier disparaît (pas de carte vide).
 *
 * Sources :
 *  · description → `product.description` (HTML sanitizé par le parent), rendue
 *    seulement si elle n'est PAS déjà affichée en entier près du prix : une
 *    description courte est l'argument de vente, elle reste au-dessus du pli et
 *    ne doit pas être répétée ici.
 *  · rubriques   → `product.sections` (migration 169), HTML riche sanitizé par
 *    le parent, dans l'ordre saisi par l'association.
 */
import type { ShopProductSection } from "~/types/shop";

const props = defineProps<{
  /** Description produit DÉJÀ sanitizée par le parent. */
  descriptionHtml: string | null;
  /** La description est-elle déjà montrée en entier au-dessus ? */
  descriptionShownAbove: boolean;
  /** Rubriques libres du produit, contenu DÉJÀ sanitizé par le parent. */
  sections: ShopProductSection[];
}>();

/** Texte utile d'un fragment HTML — sert à écarter les rubriques vides. */
function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
}

const showDescription = computed(
  () => !props.descriptionShownAbove && !!props.descriptionHtml,
);

/**
 * Rubriques réellement affichables. Le contenu arrive déjà sanitizé ; après
 * sanitization il peut devenir vide (ex. balises seules), d'où le contrôle sur
 * le texte utile et non sur la chaîne brute.
 */
const contentSections = computed(() =>
  props.sections.filter(
    (s) => s.title.trim() !== "" && stripHtml(s.content) !== "",
  ),
);

/** Rien à dire sur ce produit → le parent n'affiche pas de carte vide. */
const hasContent = computed(
  () => showDescription.value || contentSections.value.length > 0,
);
</script>

<template>
  <section
    v-if="hasContent"
    aria-labelledby="boutique-details-title"
    class="py-1"
  >
    <!-- Titre de SECTION : le plus grand des trois niveaux (section > rubrique
         > corps). L'ancien `text-sm uppercase` se confondait avec une étiquette
         de formulaire au-dessus d'un paragraphe de même taille. -->
    <h2
      id="boutique-details-title"
      class="text-xl md:text-2xl font-bold tracking-tight text-[#2d2e5c] dark:text-white"
    >
      {{ $t("boutique.productDetails.titre") }}
    </h2>

    <!-- Description longue du produit, en tête et TOUJOURS dépliée : c'est le
         propos général du produit, les rubriques ne font que le préciser. -->
    <!-- eslint-disable vue/no-v-html -->
    <!-- Reason: HTML sanitizé par le parent avant d'être passé en prop -->
    <div
      v-if="showDescription"
      class="prose prose-sm dark:prose-invert max-w-none text-[#505870] dark:text-gray-300 mt-4"
      v-html="descriptionHtml"
    />
    <!-- eslint-enable vue/no-v-html -->

    <!-- Rubriques libres de l'association (migration 169), chacune dépliante.
         La première est ouverte, les suivantes fermées. Le filet du haut est
         porté par chaque rubrique : il sépare les rubriques entre elles ET
         la première de la description. -->
    <div class="mt-4">
      <BoutiqueDetailsSection
        v-for="(section, index) in contentSections"
        :key="`${section.title}-${index}`"
        :title="section.title"
        :default-open="index === 0"
      >
        <!-- eslint-disable vue/no-v-html -->
        <!-- Reason: HTML sanitizé par le parent avant d'être passé en prop -->
        <div
          class="prose prose-sm dark:prose-invert max-w-none text-[#505870] dark:text-gray-300"
          v-html="section.content"
        />
        <!-- eslint-enable vue/no-v-html -->
      </BoutiqueDetailsSection>
    </div>
  </section>
</template>
